if (!customElements.get('eh-navigation')) {
    class EhNavigation extends HTMLElement {
        connectedCallback() {
            this.controller = new AbortController();
            const options = { signal: this.controller.signal };
            this.mobile = window.matchMedia('(max-width: 1199px)');
            this.panels = [...this.querySelectorAll('[data-panel]')];
            this.menuToggle = this.querySelector('[data-menu-toggle]');
            this.nav = this.querySelector('.eh-header__navigation');
            this.searchModal = this.querySelector('details-modal');
            this.searchModal?.querySelector('details').addEventListener('toggle', () => {
                if (this.searchModal.isOpen()) this.setView(null);
            }, options);
            const overlayColors = this.querySelector('.eh-header__overlay-colors');
            this.style.setProperty('--eh-overlay-foreground', `rgb(${getComputedStyle(overlayColors).getPropertyValue('--color-foreground')})`);
            this.addEventListener('click', (event) => {
                const toggle = event.target.closest('[data-trigger]');
                if (toggle) {
                    this.setView(this.view === toggle.dataset.trigger && !this.openedByHover ? null : toggle.dataset.trigger, true);
                    this.openedByHover = false;
                }
                else if (event.target.closest('[data-menu-toggle]')) this.setView(this.view && this.view !== 'account' ? null : 'main', true);
                else if (event.target.closest('[data-back]')) this.setView('main', true);
                else if (event.target.closest('#cart-icon-bubble')) this.setView(null);
            }, options);
            this.addEventListener('keydown', (event) => this.onKeydown(event), options);
            this.querySelectorAll('[data-group]').forEach((group) => {
                group.addEventListener('pointerenter', (event) => {
                    if (this.mobile.matches || event.pointerType !== 'mouse') return;
                    const hasDropdown = Boolean(group.querySelector('[data-trigger]'));
                    this.setView(hasDropdown ? group.dataset.group : null);
                    this.openedByHover = hasDropdown;
                }, options);
                group.addEventListener('pointerleave', (event) => {
                    if (!this.mobile.matches && event.pointerType === 'mouse') this.closeTimer = setTimeout(() => {
                        const keyboardFocusWithin = group.contains(document.activeElement) && document.activeElement.matches(':focus-visible');
                        if (this.openedByHover || !keyboardFocusWithin) this.setView(null);
                    }, 180);
                }, options);
            });
            this.querySelectorAll('[data-item-card]').forEach((item) => {
                ['pointerenter', 'focusin'].forEach((type) => item.addEventListener(type, () => this.showCard(item), options));
            });
            document.addEventListener('pointerdown', (event) => {
                if (!this.contains(event.target)) this.setView(null);
            }, options);
            document.addEventListener('focusin', (event) => {
                if (!this.mobile.matches && (!this.contains(event.target) || event.target.matches('.eh-header__link:not([data-trigger])'))) this.setView(null);
            }, options);
            window.addEventListener('scroll', () => this.updateScroll(), { ...options, passive: true });
            window.addEventListener('resize', () => {
                if (this.wasMobile !== this.mobile.matches) this.setView(null);
                this.wasMobile = this.mobile.matches;
                this.draw();
            }, options);
            this.wasMobile = this.mobile.matches;
            this.resizeObserver = new ResizeObserver(() => this.draw());
            this.panels.forEach((panel) => this.resizeObserver.observe(panel.querySelector('.eh-nav-panel__scroll')));
            if (this.nav) this.resizeObserver.observe(this.nav);
            document.addEventListener('shopify:block:select', (event) => {
                if (this.contains(event.target)) this.draw();
            }, options);
            this.updateScroll();
        }

        disconnectedCallback() {
            if (this.searchModal?.isOpen()) this.searchModal.close(false);
            this.controller?.abort();
            this.resizeObserver?.disconnect();
            clearTimeout(this.closeTimer);
            document.body.classList.remove('eh-nav-lock');
        }

        updateScroll() {
            const scrolled = window.scrollY > 20;
            if (this.classList.contains('is-scrolled') !== scrolled) {
                this.classList.toggle('is-scrolled', scrolled);
                this.draw();
            }
        }

        setView(view, keyboard = false) {
            if (view && this.searchModal?.isOpen()) this.searchModal.close(false);
            clearTimeout(this.closeTimer);
            const previous = this.view;
            this.view = view;
            this.openedByHover = false;
            const mobileMenu = this.mobile.matches && view && view !== 'account';
            this.classList.toggle('is-menu-open', Boolean(mobileMenu));
            this.classList.toggle('is-submenu-open', Boolean(mobileMenu && view !== 'main'));
            this.menuToggle?.setAttribute('aria-expanded', String(Boolean(mobileMenu)));
            this.querySelectorAll('[data-trigger]').forEach((button) => button.setAttribute('aria-expanded', String(button.dataset.trigger === view)));
            this.panels.forEach((panel) => {
                panel.hidden = panel.dataset.panel !== view;
                if (panel.hidden) this.resetCard(panel);
            });
            document.body.classList.toggle('eh-nav-lock', Boolean(this.mobile.matches && view));
            this.draw();
            if (keyboard && this.mobile.matches && view) {
                const area = view === 'main' ? this.nav : this.panels.find((panel) => !panel.hidden);
                area?.querySelector('button, a')?.focus({ preventScroll: true });
            } else if (keyboard && !view) {
                const trigger = this.mobile.matches && previous !== 'account' ? this.menuToggle : this.querySelector(`[data-trigger="${previous}"]`);
                trigger?.focus({ preventScroll: true });
            }
        }

        onKeydown(event) {
            if (event.key === 'Escape' && this.view) {
                event.preventDefault();
                this.setView(null, true);
            }
            if (event.key === 'ArrowDown' && event.target.matches('[data-trigger]')) {
                event.preventDefault();
                this.setView(event.target.dataset.trigger);
                this.panels.find((panel) => !panel.hidden)?.querySelector('a')?.focus();
            }
            if (event.key !== 'Tab' || !this.mobile.matches || !this.view) return;
            const focusable = [...this.querySelectorAll('a[href],button:not([disabled])')].filter((el) => el.getClientRects().length > 0);
            const first = focusable[0];
            const last = focusable.at(-1);
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last?.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first?.focus();
            }
        }

        showCard(item) {
            const panel = item.closest('[data-panel]');
            if (!panel) return;
            const cards = [...panel.querySelectorAll('[data-card]')];
            const selected = cards.find((card) => card.dataset.card === item.dataset.itemCard) || cards.find((card) => card.dataset.card === 'default');
            cards.forEach((card) => { card.hidden = card !== selected; });
            this.draw();
        }

        resetCard(panel) {
            panel.querySelectorAll('[data-card]').forEach((card) => { card.hidden = card.dataset.card !== 'default'; });
        }

        draw() {
            if (!this.view) return;
            const main = this.view === 'main';
            const panel = main ? this.nav : this.panels.find((item) => !item.hidden);
            if (!panel || !panel.getClientRects().length) return;
            const trigger = this.mobile.matches && this.view !== 'account' ? this.menuToggle : this.querySelector(`[data-trigger="${this.view}"]`);
            if (!trigger) return;
            if (!this.mobile.matches) {
                panel.style.removeProperty('transform');
                const bounds = panel.getBoundingClientRect();
                const shift = Math.min(0, window.innerWidth - 20 - bounds.right) + Math.max(0, 20 - bounds.left);
                if (shift) panel.style.transform = `translateX(${shift}px)`;
            } else panel.style.removeProperty('transform');
            const bounds = panel.getBoundingClientRect();
            const button = trigger.getBoundingClientRect();
            const gap = Math.max(0, bounds.top - button.bottom);
            const pillHeight = button.height;
            const top = pillHeight + gap;
            const width = bounds.width;
            const height = bounds.height + top;
            const left = button.left - bounds.left;
            const right = left + button.width;
            const center = (left + right) / 2;
            const radius = pillHeight / 2;
            const r = 24;
            const svg = panel.querySelector('.eh-nav-shape');
            if (!svg || !width || !height) return;
            svg.style.top = `-${top}px`;
            svg.setAttribute('width', width);
            svg.setAttribute('height', height);
            svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
            // One continuous outline joins the active pill and panel; no overlapping borders.
            svg.querySelector('path').setAttribute('d', `M ${r} ${top} H ${center - 10} Q ${center - 3} ${top} ${center - 3} ${top - 7} V ${pillHeight + 7} Q ${center - 3} ${pillHeight} ${center - 10} ${pillHeight} H ${left + radius} A ${radius} ${radius} 0 0 1 ${left + radius} 0 H ${right - radius} A ${radius} ${radius} 0 0 1 ${right - radius} ${pillHeight} H ${center + 10} Q ${center + 3} ${pillHeight} ${center + 3} ${pillHeight + 7} V ${top - 7} Q ${center + 3} ${top} ${center + 10} ${top} H ${width - r} Q ${width} ${top} ${width} ${top + r} V ${height - r} Q ${width} ${height} ${width - r} ${height} H ${r} Q 0 ${height} 0 ${height - r} V ${top + r} Q 0 ${top} ${r} ${top} Z`);
        }
    }
    customElements.define('eh-navigation', EhNavigation);
}
